import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import Pagination from "../../../components/ui/Pagination";
import RenderTable from "../../../components/ui/RenderTable";
import { Chip, pluralize, toQuery } from "../../../helpers/general";
import { useCookie } from "../../../hooks/useCookie";
import { useDebounce } from "../../../hooks/useDebounce";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import AdminRoute from "../../auth/RestrictedRoute";
import { cardStyle } from "../../Places/PlacesTable"; // adjust path
import TaxiSpecialZonesTable from "./TaxiSpecialZonesTable";
import { controlStyle } from "../../../helpers/constants";

export default function TaxiSpecialZones() {
  const { token } = useCookie("vToken");
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const { toastError, toastSuccess } = useToast();
  const searchQuery = useDebounce(query, 500);
  const queryClient = useQueryClient();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const endpoint = `rides/special-zones${toQuery({
    page: currentPage,
    query: searchQuery,
  })}`;

  const { data, isLoading } = useQuery<PaginatedResponse<RideSpecialZone>>({
    queryKey: ["getTaxiSpecialZones", endpoint],
    queryFn: () => apiService.MakeGetRequest(endpoint, token),
    keepPreviousData: true,
  });

  const deleteSpecialZoneMutation = useMutation({
    mutationFn: (id: string) =>
      apiService.MakeDeleteRequest(`rides/special-zones/${id}`, token),
  });

  const deleteSpecialZone = (id: string) => {
    deleteSpecialZoneMutation.mutate(id, {
      onSuccess: () => {
        toastSuccess("Zone supprimée avec succès");
        queryClient.invalidateQueries(["getTaxiSpecialZones"]);
      },
      onError: () => toastError(general.fr.message.operationFailed),
    });
  };

  const taxiSpecialZones = data?.items ?? [];
  const totalItems = data?.meta?.totalItems;

  return (
    <AdminRoute>
      <section>
        {/* Title + count + add */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Zones spéciales</h5>
            {totalItems !== undefined && (
              <Chip tone="warning">
                {totalItems} {pluralize("Zone", totalItems)}
              </Chip>
            )}
          </div>
          <Link
            to="/taxi/special-zones/new"
            className="btn btn-primary text-white rounded-pill px-3 d-flex align-items-center"
            style={{ height: 38, fontSize: 13 }}
          >
            + Ajouter une zone
          </Link>
        </div>

        {/* Search */}
        <div style={{ ...cardStyle, padding: "12px 16px" }} className="mb-3">
          <div
            className="d-flex align-items-center gap-2 px-3"
            style={{ ...controlStyle, maxWidth: 320 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              placeholder="Rechercher"
              value={query}
              onChange={(e) => setQuery(e.target.value.toLowerCase())}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              taxiSpecialZones.length > 0 ? (
                <TaxiSpecialZonesTable
                  specialZones={taxiSpecialZones}
                  deleteSpecialZone={deleteSpecialZone}
                  isDeleting={deleteSpecialZoneMutation.isLoading}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          siblingCount={1}
          totalCount={data?.meta?.totalPages || 1}
          pageSize={10}
        />
      </section>
    </AdminRoute>
  );
}
